/**
 * Shared AI Analysis Mapping Utilities
 *
 * This module provides centralized utilities for extracting and normalizing
 * AI-generated pricing data from various response formats.
 *
 * @module shared/aiMapping
 */

/**
 * Standard keys for extracting high price estimates from AI responses
 */
export const HIGH_PRICE_KEYS: string[] = [
  'high_price',
  'highprice',
  'price_high',
  'pricehigh',
  'maximum_price',
  'maximumprice',
  'upper_price',
  'upperprice',
  'highsellvalue',
  'high_estimate',
  'highestimate',
  'high_value',
  'highvalue',
  'high_bid',
  'highbid',
  'max_price',
  'maxprice',
];

/**
 * Standard keys for extracting low price estimates from AI responses
 */
export const LOW_PRICE_KEYS: string[] = [
  'low_price',
  'lowprice',
  'price_low',
  'pricelow',
  'minimum_price',
  'minimumprice',
  'lower_price',
  'lowerprice',
  'lowsellvalue',
  'low_estimate',
  'lowestimate',
  'low_value',
  'lowvalue',
  'low_bid',
  'lowbid',
  'min_price',
  'minprice',
];

/**
 * Standard keys for extracting high listings from AI responses
 */
export const HIGH_LISTINGS_KEYS: string[] = [
  'high_listings',
  'highlistings',
  'upper_listings',
  'upperlistings',
];

/**
 * Standard keys for extracting low listings from AI responses
 */
export const LOW_LISTINGS_KEYS: string[] = [
  'low_listings',
  'lowlistings',
  'lower_listings',
  'lowerlistings',
];

/**
 * Standard keys for extracting price status from AI responses
 */
export const PRICE_STATUS_KEYS: string[] = [
  'price_status',
  'pricestatus',
  'pricing_status',
  'valuation_status',
  'pricingstatus',
  'valuationstatus',
];

/**
 * Combined AI analysis keys for all fields
 */
export const AI_ANALYSIS_KEYS = {
  highPrice: HIGH_PRICE_KEYS,
  lowPrice: LOW_PRICE_KEYS,
  highListings: HIGH_LISTINGS_KEYS,
  lowListings: LOW_LISTINGS_KEYS,
  priceStatus: PRICE_STATUS_KEYS,
} as const;

/**
 * Maximum recursion depth for AI value extraction
 */
export const MAX_AI_EXTRACTION_DEPTH = 12;

/**
 * Canonicalize an AI response key for case-insensitive comparison
 * Removes all non-alphanumeric characters and converts to lowercase
 *
 * @param key - The key to canonicalize
 * @returns Canonicalized key string
 *
 * @example
 * canonicalizeAiKey('high_price') // 'highprice'
 * canonicalizeAiKey('High-Price') // 'highprice'
 */
export function canonicalizeAiKey(key: unknown): string {
  if (typeof key !== 'string') {
    return '';
  }
  return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/**
 * Type guard to check if value is a plain object
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively extract a value from nested AI response data
 *
 * @param value - The value to search
 * @param keys - Canonicalized keys to search for
 * @param depth - Current recursion depth
 * @returns The first matching value found, or undefined
 */
function extractAiValueRecursive(value: unknown, keys: string[], depth: number): unknown {
  if (depth > MAX_AI_EXTRACTION_DEPTH || value == null) {
    return undefined;
  }

  // Search arrays recursively
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const nested = extractAiValueRecursive(value[i], keys, depth + 1);
      if (nested !== undefined) {
        return nested;
      }
    }
    return undefined;
  }

  // Search objects for matching keys
  if (isObject(value)) {
    const entries = Object.entries(value);

    // First pass: look for direct key matches
    for (let i = 0; i < entries.length; i++) {
      const [key, val] = entries[i];
      if (keys.includes(canonicalizeAiKey(key))) {
        return val;
      }
    }

    // Second pass: recurse into nested objects
    for (let i = 0; i < entries.length; i++) {
      const nested = extractAiValueRecursive(entries[i][1], keys, depth + 1);
      if (nested !== undefined) {
        return nested;
      }
    }
    return undefined;
  }

  return undefined;
}

/**
 * Extract a value from AI response data using a list of possible keys
 *
 * @param source - The AI response data to search
 * @param keys - List of possible key names to search for
 * @returns The first matching value found, or undefined
 *
 * @example
 * const data = { high_estimate: { amount: 1000 } }
 * extractAiValue(data, HIGH_PRICE_KEYS) // { amount: 1000 }
 */
export function extractAiValue(source: unknown, keys: readonly string[]): unknown {
  if (!isObject(source)) {
    return undefined;
  }
  const normalizedKeys = keys.map(canonicalizeAiKey);
  return extractAiValueRecursive(source, normalizedKeys, 0);
}

/**
 * Find the first matching key in an object (case-insensitive)
 *
 * @param obj - Object to search
 * @param keys - Keys to look for
 * @returns The actual key name found in the object, or undefined
 */
export function findMatchingKey(
  obj: Record<string, unknown>,
  keys: readonly string[]
): string | undefined {
  const normalizedKeys = keys.map(canonicalizeAiKey);
  const entries = Object.entries(obj);

  for (let i = 0; i < entries.length; i++) {
    const [key] = entries[i];
    if (normalizedKeys.includes(canonicalizeAiKey(key))) {
      return key;
    }
  }

  return undefined;
}

/**
 * Normalize listings array from AI response
 * Handles both array and string inputs, filtering out empty values
 *
 * @param candidate - Raw listings data from AI
 * @returns Normalized array of listing strings, or undefined
 *
 * @example
 * normalizeListings(['item1', '', 'item2']) // ['item1', 'item2']
 * normalizeListings('single item') // ['single item']
 */
export function normalizeListings(candidate: unknown): string[] | undefined {
  if (candidate == null) {
    return undefined;
  }

  if (Array.isArray(candidate)) {
    const results: string[] = [];
    for (let i = 0; i < candidate.length; i++) {
      const entry = candidate[i];
      if (typeof entry === 'string') {
        const trimmed = entry.trim();
        if (trimmed) {
          results.push(trimmed);
        }
      }
    }
    return results.length ? results : undefined;
  }

  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    return trimmed ? [trimmed] : undefined;
  }

  return undefined;
}

/**
 * Extract value case-insensitively from an object
 * Used by backend API for compatibility
 *
 * @param obj - Object to search
 * @param keys - Keys to search for
 * @returns First matching value or undefined
 */
export function extractValueCaseInsensitive(
  obj: Record<string, unknown>,
  keys: readonly string[]
): unknown {
  return extractAiValue(obj, keys);
}
